/**
 * yanamas Inbox — "things yanamas heard that may matter, but are not goals
 * or tasks yet" (blueprint §7), plus the people/places yanamas noticed and
 * is asking to remember (§11).
 *
 * Deliberate UX rules from the blueprint:
 *   - a few relevant cards, NEVER an inventory count (§7.1 — counts are
 *     guilt UI);
 *   - dismissed is final; snooze is easy; trash is available;
 *   - world candidates are surfaced for confirmation, never auto-added.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import {
  Archive,
  Check,
  Clock,
  Inbox,
  MapPin,
  MessageCircle,
  Sparkles,
  Trash2,
  User,
  X,
} from 'lucide-react'
import {
  hortonGuidanceService,
  type OpenThread,
  type WorldCandidate,
} from '../services/hortonGuidanceService'
import { useUserTimezone } from '../hooks/useUserTimezone'
import { BRAND } from '../config/brand'

const THREAD_TYPE_LABEL: Record<string, string> = {
  dream: 'could become a dream',
  aspiration: 'could become an aspiration',
  goal: 'could become a goal',
  milestone: 'could become a milestone',
  task: 'could become a task',
  habit: 'could become a habit',
  skill: 'could become a skill',
  blocker_annotation: 'something in the way',
}

function ThreadCard({
  thread,
  onAction,
  onConvert,
  onOpenChat,
  onWorkNewChat,
  variant = 'inbox',
}: {
  thread: OpenThread
  onAction: (id: string, action: 'explore' | 'snooze' | 'unsnooze' | 'dismiss' | 'trash') => void
  onConvert: (thread: OpenThread) => void
  onOpenChat: (chatId: string) => void
  onWorkNewChat: (thread: OpenThread) => void
  /** 'saved' = the Saved-for-later shelf: shows the return date and swaps
   *  "Later" for "Bring back now" (unsnooze). */
  variant?: 'inbox' | 'saved'
}) {
  const [busy, setBusy] = useState(false)
  const { formatDateTime } = useUserTimezone()
  const act = async (fn: () => Promise<void>) => {
    setBusy(true)
    try {
      await fn()
    } finally {
      setBusy(false)
    }
  }
  return (
    <Card className="border-l-4 border-l-amber-400">
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium">{thread.title}</div>
            <p className="mt-1 text-sm text-muted-foreground">{thread.what_horton_heard}</p>
            {thread.possible_lifemap_type && (
              <span className="mt-2 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                {THREAD_TYPE_LABEL[thread.possible_lifemap_type] ?? thread.possible_lifemap_type}
              </span>
            )}
            {thread.missing_to_make_actionable.length > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                To make it real, {BRAND.assistant} would ask about:{' '}
                {thread.missing_to_make_actionable.slice(0, 3).join(', ')}
              </p>
            )}
            {/* Provenance (owner ask 2026-07-05): when it was noticed, in the
                user's timezone, with a link back to the source conversation. */}
            <p className="mt-2 text-xs text-muted-foreground">
              Noticed {formatDateTime(thread.created_at)}
              {thread.source_chat_id && thread.source_chat_title && (
                <>
                  {' · from '}
                  <button
                    type="button"
                    className="underline hover:text-foreground"
                    onClick={() => onOpenChat(thread.source_chat_id!)}
                  >
                    {thread.source_chat_title}
                  </button>
                </>
              )}
              {variant === 'saved' && thread.snoozed_until && (
                <> · returns on its own {formatDateTime(thread.snoozed_until)}</>
              )}
            </p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={busy}
            onClick={() => act(async () => onConvert(thread))}
          >
            <Sparkles className="mr-1 h-4 w-4" /> Make it real
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => onWorkNewChat(thread)}
          >
            <MessageCircle className="mr-1 h-4 w-4" /> Work on it in a new chat
          </Button>
          {variant === 'saved' ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => act(async () => onAction(thread.id, 'unsnooze'))}
            >
              <Clock className="mr-1 h-4 w-4" /> Bring back now
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => act(async () => onAction(thread.id, 'snooze'))}
            >
              <Clock className="mr-1 h-4 w-4" /> Later
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => act(async () => onAction(thread.id, 'dismiss'))}
          >
            <Archive className="mr-1 h-4 w-4" /> Not for me
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => act(async () => onAction(thread.id, 'trash'))}
          >
            <Trash2 className="mr-1 h-4 w-4" /> Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function WorldCandidateCard({
  candidate,
  onAction,
  onOpenChat,
}: {
  candidate: WorldCandidate
  onAction: (id: string, action: 'confirm' | 'dismiss') => void
  onOpenChat: (chatId: string) => void
}) {
  const [busy, setBusy] = useState(false)
  const { formatDateTime } = useUserTimezone()
  const act = async (action: 'confirm' | 'dismiss') => {
    setBusy(true)
    try {
      await onAction(candidate.id, action)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Card className="border-l-4 border-l-sky-400">
      <CardContent className="pt-4">
        <div className="flex items-center gap-2">
          {candidate.kind === 'person' ? (
            <User className="h-4 w-4 text-sky-500" />
          ) : (
            <MapPin className="h-4 w-4 text-sky-500" />
          )}
          <span className="font-medium">{candidate.name_or_alias}</span>
          {candidate.relationship_as_user_stated && (
            <span className="text-sm text-muted-foreground">
              — {candidate.relationship_as_user_stated}
            </span>
          )}
          {candidate.place_kind_hint && (
            <span className="text-sm text-muted-foreground">— {candidate.place_kind_hint}</span>
          )}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {BRAND.assistant} noticed {candidate.kind === 'person' ? 'this person' : 'this place'} in your
          conversations. Add {candidate.kind === 'person' ? 'them' : 'it'} to your world?
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Noticed {formatDateTime(candidate.created_at)}
          {candidate.source_chat_id && candidate.source_chat_title && (
            <>
              {' · from '}
              <button
                type="button"
                className="underline hover:text-foreground"
                onClick={() => onOpenChat(candidate.source_chat_id!)}
              >
                {candidate.source_chat_title}
              </button>
            </>
          )}
        </p>
        <div className="mt-3 flex gap-2">
          <Button size="sm" disabled={busy} onClick={() => act('confirm')}>
            <Check className="mr-1 h-4 w-4" /> Yes, remember
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => act('dismiss')}>
            <X className="mr-1 h-4 w-4" /> No thanks
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

export function HortonInbox({ embedded = false }: { embedded?: boolean } = {}) {
  const [threads, setThreads] = useState<OpenThread[]>([])
  const [candidates, setCandidates] = useState<WorldCandidate[]>([])
  const [goalsNeedingPath, setGoalsNeedingPath] = useState<Array<{ id: number; title: string }>>([])
  const [engineDown, setEngineDown] = useState(false)
  const [loading, setLoading] = useState(true)
  const [notice, setNotice] = useState<string | null>(null)
  // "Saved for later" shelf (owner ask 2026-07-05): snoozed threads must be
  // recallable, not vanished for a week.
  const [activeTab, setActiveTab] = useState<'inbox' | 'saved'>('inbox')
  const [savedThreads, setSavedThreads] = useState<OpenThread[]>([])
  const navigate = useNavigate()

  const load = useCallback(async () => {
    setLoading(true)
    // Goals saved with no actionable path (goal-journey backstop). This is a
    // saas-core feed, independent of the guidance engine, so it renders even
    // when the engine is down.
    try {
      const resp = await fetch('/api/hierarchy/goals/needs-journey', { credentials: 'include' })
      if (resp.ok) {
        const data = await resp.json()
        setGoalsNeedingPath(Array.isArray(data.goals) ? data.goals : [])
      }
    } catch {
      /* non-fatal: the section just won't show */
    }
    try {
      // Gate out ONLY the conversation currently open in the chat (the ephemeral
      // marker, cleared on leaving the chat) — so a conversation you've left
      // graduates its held items here even before you commit or start a new one.
      const activeChatId = sessionStorage.getItem('horton_active_chat_id') || undefined
      const [inbox, saved] = await Promise.all([
        hortonGuidanceService.getInbox(6, activeChatId),
        hortonGuidanceService.getSaved().catch(() => ({ threads: [] as OpenThread[] })),
      ])
      setThreads(inbox.threads)
      setCandidates(inbox.world_candidates)
      setSavedThreads(saved.threads)
      setEngineDown(inbox.engineUnavailable === true)
    } catch {
      setEngineDown(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const flash = (message: string) => {
    setNotice(message)
    window.setTimeout(() => setNotice(null), 4000)
  }

  const handleThreadAction = async (
    id: string,
    action: 'explore' | 'snooze' | 'unsnooze' | 'dismiss' | 'trash',
  ) => {
    await hortonGuidanceService.threadAction(id, action, action === 'snooze' ? { snooze_days: 7 } : {})
    if (action === 'unsnooze') {
      // Saved → Inbox: move the card across without a refetch.
      const t = savedThreads.find((s) => s.id === id)
      setSavedThreads((prev) => prev.filter((s) => s.id !== id))
      if (t) setThreads((prev) => [{ ...t, status: 'captured', snoozed_until: null }, ...prev])
      flash('Back in your Inbox.')
      setActiveTab('inbox')
      return
    }
    setThreads((prev) => prev.filter((t) => t.id !== id))
    setSavedThreads((prev) => prev.filter((t) => t.id !== id))
    if (action === 'snooze') {
      // Keep it recallable: it moves to the Saved for later shelf.
      const t = threads.find((s) => s.id === id)
      if (t) setSavedThreads((prev) => [{ ...t, status: 'snoozed' }, ...prev])
      flash("Snoozed — it's under Saved for later whenever you want it.")
    }
    if (action === 'dismiss') flash(`Dismissed — ${BRAND.assistant} won't bring this up again.`)
    if (action === 'trash') flash('Deleted.')
  }

  const handleConvert = async (thread: OpenThread) => {
    const result = await hortonGuidanceService.convertThread(thread.id)
    setThreads((prev) => prev.filter((t) => t.id !== thread.id))
    setSavedThreads((prev) => prev.filter((t) => t.id !== thread.id))
    flash(result.node_id ? `Added "${thread.title}" to your Life Map.` : 'Converted.')
  }

  const handleWorldAction = async (id: string, action: 'confirm' | 'dismiss') => {
    await hortonGuidanceService.worldAction(id, action)
    setCandidates((prev) => prev.filter((c) => c.id !== id))
    flash(action === 'confirm' ? 'Added to your world.' : `Okay — ${BRAND.assistant} won't track that.`)
  }

  /** Resume the conversation an item was discovered in — same continuity
   *  mechanism the chat uses (sessionStorage chat pointer, HortonChat adopts
   *  it on mount). */
  const openOriginalChat = (chatId: string) => {
    sessionStorage.setItem('horton_current_chat_id', chatId)
    navigate('/horton-ai')
  }

  /** Start a FRESH conversation about a held item: HortonChat consumes the
   *  intent on mount (mints a new chat_id + prefills the opener). */
  const workOnInNewChat = (thread: OpenThread) => {
    sessionStorage.setItem(
      'horton_new_chat_intent',
      JSON.stringify({
        opener: `I'd like to work on "${thread.title}".${
          thread.what_horton_heard ? ` Earlier I mentioned: ${thread.what_horton_heard}` : ''
        } Help me shape it.`,
      }),
    )
    navigate('/horton-ai')
  }

  return (
    <div className={embedded ? 'space-y-5' : 'mx-auto max-w-3xl space-y-5 p-4'}>
      {/* When this renders as a tab of the one inbox, the page above already
          carries the title (2026-09-06: there were three separate inboxes). */}
      {!embedded && (
        <div className="flex items-baseline gap-2 min-w-0">
          <h1 className="flex items-center gap-2 text-lg font-bold text-primary">
            <Inbox className="h-4 w-4" /> To decide
          </h1>
          <p className="hidden md:block text-xs text-muted truncate">
            held safely until you decide — nothing here is a commitment
          </p>
        </div>
      )}

      {notice && (
        <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">
          {notice}
        </div>
      )}

      {/* Inbox | Saved for later (owner ask 2026-07-05: "Later" must be
          recallable, not a week-long disappearance). */}
      <div className="flex gap-1 border-b">
        {([
          { id: 'inbox' as const, label: 'Inbox' },
          { id: 'saved' as const, label: 'Saved for later' },
        ]).map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'border-b-2 border-brand text-brand'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
            {tab.id === 'saved' && savedThreads.length > 0 && (
              <span className="ml-1.5 text-xs text-muted-foreground">({savedThreads.length})</span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'inbox' && goalsNeedingPath.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            Goals needing a path
          </h2>
          {goalsNeedingPath.map((g) => (
            <Card key={g.id}>
              <CardContent className="flex items-center justify-between gap-3 pt-4">
                <div className="flex items-start gap-2">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                  <div>
                    <div className="font-medium">{g.title}</div>
                    <p className="text-sm text-muted-foreground">
                      Saved without steps yet — map a path you can review and approve.
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="shrink-0"
                  onClick={() => navigate(`/goals/${g.id}?action=breakdown`)}
                >
                  Map this goal's path
                </Button>
              </CardContent>
            </Card>
          ))}
        </section>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Listening…</p>
      ) : engineDown ? (
        <Card>
          <CardContent className="pt-4 text-sm text-muted-foreground">
            {BRAND.assistant}' guidance engine isn't reachable right now. Your conversations still work —
            held threads will show up here once it's back.
          </CardContent>
        </Card>
      ) : activeTab === 'saved' ? (
        <section className="space-y-3">
          <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            Saved for later
          </h2>
          {savedThreads.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-normal text-muted-foreground">
                  Nothing saved — when you tap "Later" on a held thought, it waits here until
                  you're ready (or comes back on its own).
                </CardTitle>
              </CardHeader>
            </Card>
          ) : (
            savedThreads.map((thread) => (
              <ThreadCard
                key={thread.id}
                thread={thread}
                variant="saved"
                onAction={handleThreadAction}
                onConvert={handleConvert}
                onOpenChat={openOriginalChat}
                onWorkNewChat={workOnInNewChat}
              />
            ))
          )}
        </section>
      ) : (
        <>
          {candidates.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                People &amp; places {BRAND.assistant} noticed
              </h2>
              {candidates.map((candidate) => (
                <WorldCandidateCard
                  key={candidate.id}
                  candidate={candidate}
                  onAction={handleWorldAction}
                  onOpenChat={openOriginalChat}
                />
              ))}
            </section>
          )}

          <section className="space-y-3">
            <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              Open threads
            </h2>
            {threads.length === 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-normal text-muted-foreground">
                    Nothing held right now — when a conversation carries something meaningful but
                    unshaped, {BRAND.assistant} will keep it here for you.
                  </CardTitle>
                </CardHeader>
              </Card>
            ) : (
              threads.map((thread) => (
                <ThreadCard
                  key={thread.id}
                  thread={thread}
                  onAction={handleThreadAction}
                  onConvert={handleConvert}
                  onOpenChat={openOriginalChat}
                  onWorkNewChat={workOnInNewChat}
                />
              ))
            )}
          </section>
        </>
      )}
    </div>
  )
}
